import ParameterValidator from '@mvps-genesis/parameter-validator';
import PhotoSwipeTemplate from './PhotoSwipeTemplate.html';

export default class ProductImageGallery {

	thumbnailSlider;

	primarySlider;

	imPrevData;

	// eslint-disable-next-line max-lines-per-function
	constructor ({
		productLayoutImageSlider = '#js-product-layout-image-slider',
		mainImagecarouselOptions = {
			arrows: false,
			breakpoints: {
				959: {
					pagination: true
				}
			},
			lazyLoad: 'nearby',
			pagination: false,
			preloadPages: 0,
			type: 'loop'
		},
		thumbnailCarouselOptions = {
			arrows: true,
			autoWidth: true,
			breakpoints: {
				959: {
					perPage: 1
				}
			},
			gap: '0.5rem',
			isNavigation: true,
			lazyLoad: 'nearby',
			pagination: false,
			perPage: 5,
			preloadPages: 0,
			type: 'loop'
		},
		photoSwipeWrapSelector = 'js-photoswipe-wrap',
		thumbnailsSliderSelector = 'js-thumbnails-slider',
		carouselSlidesSelector = '.g-carousel__slides',
		videos = []
	} = {}) {

		// Create validator
		this._validator = new ParameterValidator(this);

		// Validate and set properties
		this.productLayoutImageSlider = this._validator.isElement('options.productLayoutImageSlider', productLayoutImageSlider);
		this.mainImagecarouselOptions = this._validator.isObject('options.mainImagecarouselOptions', mainImagecarouselOptions);
		this.thumbnailCarouselOptions = this._validator.isObject('options.thumbnailCarouselOptions', thumbnailCarouselOptions);
		this.photoSwipeWrapSelector = this._validator.isString('options.photoSwipeWrapSelector', photoSwipeWrapSelector);
		this.thumbnailsSliderSelector = this._validator.isString('options.thumbnailsSliderSelector', thumbnailsSliderSelector);
		this.carouselSlidesSelector = this._validator.isString('options.carouselSlidesSelector', carouselSlidesSelector);
		this.videos = this._validator.isArray('options.videos', videos);

		this.thumbnailSlider = null;
		this.primarySlider = null;
		this.imPrevData = '';

		this.init();

	}

	init () {

		if (typeof window.ImageMachine !== 'undefined') {
			this.setImageMachineOverrides();

			return;
		}

		window.addEventListener('imagemachine_override', () => {
			this.setImageMachineOverrides();
		});
	}

	setImageMachineOverrides () {
		const self = this;

		window.ImageMachine.prototype.oninitialize = function (data) {
			// eslint-disable-next-line consistent-this
			const imageMachine = this;

			// Set elements if not available
			if (!imageMachine.imageSliderSlider || !imageMachine.imageSliderSlides || !imageMachine.thumbnailSlider) {
				imageMachine.imageSliderSlider = self.productLayoutImageSlider;
				imageMachine.imageSliderSlides = null;

				if (imageMachine.imageSliderSlider) {
					imageMachine.imageSliderSlides = imageMachine.imageSliderSlider.querySelector(self.carouselSlidesSelector);
				}

				imageMachine.thumbnailSlider = document.getElementById(self.thumbnailsSliderSelector);
			}

			if (imageMachine.thumbnails === 'undefined' && imageMachine.thumbnailSlider instanceof HTMLElement) {
				imageMachine.thumbnails = imageMachine.thumbnailSlider.querySelector(self.carouselSlidesSelector);
			}

			if (!data || !self.imageMachineDataHasChanged(data, self.imPrevData)) {
				return;
			}

			data.push(...self.getVideoData());

			if (data[0] && data[0].image_data && data[0].image_data[2]) {
				const img = new Image();

				img.onload = function () {
					self.initializeGalleryFromData(imageMachine, data);
				};

				img.src = data[0].image_data[2];
			} else {
				self.initializeGalleryFromData(imageMachine, data);
			}
		};
	}

	imageMachineDataHasChanged (newData, previousData) {
		return !previousData || JSON.stringify(newData) !== JSON.stringify(previousData);
	}

	getVideoData () {

		let videoData = [];

		if (!this.videos.length) {
			return videoData;
		}

		for (const video of this.videos) {
			videoData.push({
				/* eslint-disable camelcase */
				image_data: [
					`https://www.youtube.com/embed/${video}?modestbranding=1&enablejsapi=1`,
					`https://img.youtube.com/vi/${video}/hqdefault.jpg`
				],
				type_code: 'video_embed'
				/* eslint-enable camelcase */
			});
		}

		return videoData;
	}

	initializeGalleryFromData (imageMachine, data) {
		// eslint-disable-next-line new-cap
		imageMachine.Initialize(data);
		this.createProductImageCarousels(imageMachine.thumbnailSlider, imageMachine.imageSliderSlider, data);
	}

	createProductImageCarousels (thumbnailsElement, mainElement, imageData) {
		if (!thumbnailsElement || !mainElement) {
			return;
		}

		this.createThumbnailCarousel(thumbnailsElement);
		this.createMainCarousel(mainElement, imageData);
	};

	async createMainCarousel (mainElement, imageData) {
		const {Carousel} = await import('extensions/carousel');

		if (this.primarySlider && this.primarySlider.slider) {
			this.primarySlider.slider.destroy('completely');
		}

		if (this.thumbnailSlider.slideCount === 0) {

			if (imageData?.length) {

				/* Slider won't be initalized, so set the click Events for the images */
				for (const image of this.productLayoutImageSlider?.querySelectorAll?.(`${this.carouselSlidesSelector} .g-carousel__slide`)) {
					image.addEventListener('click', () => this.mainImageClicked(image, imageData));
				}
			}

			return;
		}

		this.primarySlider = new Carousel(mainElement, this.mainImagecarouselOptions);

		this.primarySlider.slider.on('moved', () => {
			if (!this.primarySlider.container) {
				return;
			}

			this.pauseProductVideos(this.primarySlider.container.querySelectorAll('iframe'));
		});

		this.primarySlider.slider.sync(this.thumbnailSlider.slider).mount();

		// Attach click events to Slides
		this.primarySlider.slider.on('click', (slide) => this.mainImageClicked(slide.slide, imageData));
	}

	async createThumbnailCarousel (thumbnailsElement) {
		const {Carousel} = await import('extensions/carousel');

		if (this.thumbnailSlider && this.thumbnailSlider.slider) {
			this.thumbnailSlider.slider.destroy('completely');
		}

		const thumbnailsCount = thumbnailsElement.getElementsByClassName('g-carousel__slide');
		const thumbnailCarouselOptions = this.thumbnailCarouselOptions;
		const hideArrows = thumbnailsCount.length <= thumbnailCarouselOptions.perPage;

		thumbnailCarouselOptions.arrows = !hideArrows;
		thumbnailCarouselOptions.type = hideArrows ? 'slide' : 'loop';
		thumbnailCarouselOptions.autoWidth = hideArrows;

		this.thumbnailSlider = new Carousel(thumbnailsElement, thumbnailCarouselOptions);

		if (hideArrows) {
			thumbnailsElement.classList.add('g-product-layout-images__arrows-hidden');
			thumbnailsElement.classList.add('g-product-thumbnails__centered');
		} else {
			thumbnailsElement.classList.remove('g-product-layout-images__arrows-hidden');
			thumbnailsElement.classList.remove('g-product-thumbnails__centered');
		}

		if (this.thumbnailSlider.slideCount > 0) {
			this.thumbnailSlider.slider.mount();
		}
	}

	async mainImageClicked (elem, imageData) {
		const self = this;

		const {default: PhotoSwipe} = await import('photoswipe/dist/photoswipe');
		// eslint-disable-next-line camelcase
		const {default: PhotoSwipeUI_Default} = await import('photoswipe/dist/photoswipe-ui-default');
		await import('photoswipe/dist/photoswipe.css');
		await import('photoswipe/dist/default-skin/default-skin.css');

		const items = [];
		const currentIndex = parseInt(elem.getAttribute('data-index'), 10);

		for (const image of imageData) {
			if (!image.image_data) {
				continue;
			}

			let item = {
				h: 0,
				src: image.image_data[2] ? image.image_data[2] : image.image_data[1],
				w: 0
			};

			if (image.type_code === 'video_embed' && image.image_data[0]) {
				item = {
					html: `
						<div class="photoswipe--iframe-wrap">
							<div class="u-responsive-embed u-responsive-embed--16by9">
								<iframe class="photoswipe--iframe" src="${image.image_data[0]}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
							</div>
						</div>`
				};
			}

			items.push(item);
		}

		const gallery = new PhotoSwipe(self.getPhotoSwipeElement(), PhotoSwipeUI_Default, items, {
			closeOnScroll: false,
			history: false,
			index: currentIndex
		});

		this.bindGalleryEvents(gallery);

		gallery.init();
	}

	bindGalleryEvents (gallery) {

		/* Dynamically update Image sizes */
		gallery.listen('imageLoadComplete', (index, item) => {
			if (!item.src) {
				return;
			}

			if (item.w > 1 && item.h > 1) {
				return;
			}

			this.updateGalleryItem(gallery, item);
		});

		gallery.listen('afterChange', () => {
			if (this.thumbnailSlider && this.thumbnailSlider?.slideCount && this.thumbnailSlider.slider) {
				this.thumbnailSlider.slider.go(gallery.getCurrentIndex(), false);
			}
		});

		gallery.listen('close', () => {
			this.pauseProductVideos(gallery.template.querySelectorAll('iframe'));
		});

		gallery.listen('beforeChange', () => {
			this.pauseProductVideos(gallery.template.querySelectorAll('iframe'));
		});

	}

	async updateGalleryItem (gallery, item) {
		var img = new Image();

		img.onload = await function () {
			item.w = img.naturalWidth;
			item.h = img.naturalHeight;

			gallery.updateSize(true);
		};

		img.src = item.src;
	}

	pauseProductVideos (iframes) {
		for (const iframe of iframes) {
			if (!iframe.contentWindow) {
				continue;
			}

			// Specific to Youtube's API: https://developers.google.com/youtube/iframe_api_reference
			iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
		}
	}

	getPhotoSwipeElement () {
		let elem = document.getElementById(this.photoSwipeWrapSelector);

		if (elem) {
			return elem;
		}

		// Markup taken from https://photoswipe.com/documentation/getting-started.html
		elem = document.createElement('div');
		elem.setAttribute('id', this.photoSwipeWrapSelector);
		elem.classList.add('pswp');
		elem.setAttribute('tabindex', -1);
		elem.setAttribute('role', 'dialog');
		elem.setAttribute('aria-hidden', 'true');

		elem.innerHTML = PhotoSwipeTemplate;
		document.body.appendChild(elem);

		return elem;
	}

}
